import { useMemo, useState, useCallback, useEffect } from 'react';
import style from './PromptModal.module.scss';
import Modal from '../../../../ui/modal/Modal';
import { Prompt } from '../../models/PromptTypes';
import useLabels from '../../../../hooks/useLabels';
import useInfo from '../../../../hooks/useInfo';
import { usePrompt } from '../../contexts/PromptContext';
import TwoColumnGrid from '../../../dashboards/admin/elements/grid/two-column-grid/TwoColumnGrid';
import Column from '../../../dashboards/admin/elements/grid/column/Column';
import Input from '../../../../ui/input/Input';
import { getClassNames } from '../../../../helpers/classHelpers';
import Textarea from '../../../../ui/textarea/Textarea';
import { SelectStatic } from '../../../../ui/select/Select';
import Checkbox from '../../../../ui/checkbox/Checkbox';
import Button, { ButtonThemes } from '../../../../ui/button/Button';
import ButtonAsync from '../../../../ui/button/ButtonAsync';
import { categoryOptions, tagOptions } from '../../config';
import { useChat } from '../../../../contexts/chat/ChatContext';
import { useNavigate } from 'react-router-dom';
import { AppRoute } from '../../../../router/Routing';
import MultiSelect from '../../../../ui/select/MultiSelect';
import { CustomSelectOptionBaseType } from '@danfoss/mosaic-core';
import { handleSelectChange } from '../../helpers/PromptHelper';
import FilterChipSet from '../../../../ui/chip/filter-chip.tsx/FilterChipSet';
import MarkdownEditor from '../../../../ui/markdown-editor/markdownEditor';
import Alert from '../../../../ui/alert/Alert';

type PromptAddModalProps = {
    isOpen: boolean;
    onClose: () => void;
    initialValue?: Prompt;
    mode?: 'edit' | 'copy' | 'add';
    allowTryPrompt?: boolean;
    onAfterSubmit?: () => void;
};

const PromptModal: React.FC<PromptAddModalProps> = ({ isOpen, onClose, initialValue, mode = 'edit', allowTryPrompt = true, onAfterSubmit }) => {
    const labels = useLabels();
    const { createPrompt, updatePrompt, copyPrompt } = usePrompt();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [content, setContent] = useState('');
    const [assistantType, setAssistantType] = useState('');
    const [category, setCategory] = useState('');
    const [tags, setTags] = useState<string[]>([]);
    const [isPublic, setIsPublic] = useState(false);
    const [showPublicWarning, setShowPublicWarning] = useState(false);
    const navigate = useNavigate();
    const { persistent: { mergeInput } } = useChat();
    const { allowedPersonaOptions } = useInfo();

    const l = useMemo(() => ({
        addHeadline: labels.promptModalAddPrompt,
        editHeadline: labels.promptModalEditPrompt,
        copyHeadline: labels.promptModalCopyPrompt,
        namePlaceholder: labels.promptModalTitle,
        descriptionPlaceholder: labels.promptModalDescription,
        contentPlaceholder: labels.promptModalContent,
        assistantType: labels.filterAssistant,
        category: labels.filterTask,
        isPublic: labels.promptModalIsPublic,
        isPublicWarning: labels.promptModalIsPublicWarning,
        cancel: labels.cancel,
        submit: labels.save,
        invalidFileNameError: labels.invalidFileNameError,
        invalidFileNameLength: labels.invalidFileNameLength,
        invalidFileNameChars: labels.invalidFileNameChars,
        saveAndTryPromptButton: labels.promptModalSaveAndTryPromptButton
    }), [labels]);


    useEffect(() => {
        if (isOpen && allowedPersonaOptions.length > 0) {
            setName(initialValue?.title || '');
            setDescription(initialValue?.description || '');
            setContent(initialValue?.content || '');
            setAssistantType((initialValue?.assistant_key || allowedPersonaOptions[0]?.key) ?? '');
            setCategory(initialValue?.category || '');
            setIsPublic(initialValue?.is_public ?? false);
            setTags(initialValue?.tags ? initialValue.tags.split(',').map(tag => tag.trim()) : []);
            setShowPublicWarning(false);
        }
    }, [isOpen, initialValue, allowedPersonaOptions]);

    const isFormValid = useMemo(() => {
        return name.trim() && content.trim() && assistantType;
    }, [name, content, assistantType]);

    const savePrompt = useCallback(async () => {
        if (!isFormValid) return;

        const payload = {
            title: name,
            description: description,
            content: content,
            category: category,
            is_public: isPublic,
            tags: tags.join(','),
        };

        let savedPrompt: Prompt | undefined;


        if (mode === 'copy' && initialValue?.key) {
            const res = await copyPrompt(initialValue.assistant_key, initialValue.key, payload);
            if (res.success) savedPrompt = res.data;
        } else if (initialValue?.key) {
            const res = await updatePrompt(initialValue.assistant_key, initialValue.key, payload);
            if (res.success) {
                savedPrompt = {
                    ...initialValue,
                    ...payload
                };
            }
        } else {
            savedPrompt = await createPrompt(assistantType, payload);
        }

        onAfterSubmit?.();
        return savedPrompt;
    }, [isFormValid, name, description, content, category, isPublic, tags, mode, initialValue, onAfterSubmit, copyPrompt, updatePrompt, createPrompt, assistantType]);

    const runPrompt = useCallback((content: string, assistantKey: string, promptKey: string) => {
        mergeInput(assistantKey, { message: content, promptTemplateId: promptKey });
        const persona = allowedPersonaOptions.find(p => p.key === assistantKey);
        const route = persona?.route ?? assistantKey;
        navigate(route ? "/" + route : AppRoute.chat);
    }, [mergeInput, navigate, allowedPersonaOptions]);

    const handleTagsChange = useCallback(
        (option?: string | string[] | CustomSelectOptionBaseType | CustomSelectOptionBaseType[]) => {
            handleSelectChange(option, setTags);
        }, []
    );

    const handleRemoveTag = useCallback((id: string) => {
        setTags(prevTags => prevTags.filter(tag => tag !== id));
    }, []);

    const handleIsPublicChange = useCallback((newValue: boolean) => {
        const previousValue = isPublic;
        setIsPublic(newValue);

        if (!previousValue && newValue) {
            setShowPublicWarning(true);
        } else {
            setShowPublicWarning(false);
        }
    }, [isPublic]);

    const handleSaveAndTry = useCallback(async () => {
        const savedPrompt = await savePrompt();
        runPrompt(content, assistantType, savedPrompt?.key || '');
        onClose();
    }, [savePrompt, runPrompt, content, assistantType, onClose]);

    const handleSubmit = useCallback(async () => {
        await savePrompt();
        onClose();
    }, [savePrompt, onClose]);

    return (
        <Modal
            isOpen={isOpen}
            headline={mode === 'copy' ? l.copyHeadline : (initialValue?.key ? l.editHeadline : l.addHeadline)}
            className={style['prompt-modal']}
            closeOnEscape
            closeOnBackdropClick
            onClose={onClose}
            size='large'
            body={
                <TwoColumnGrid
                    left={<Column
                        headline='' content={
                            <div className={style['input-container']}>
                                <fieldset className={getClassNames([style['df-form-field']])}>
                                    <Input
                                        label={l.namePlaceholder}
                                        placeholder={l.namePlaceholder}
                                        value={name}
                                        onChange={setName}
                                        className={style.input}
                                    />
                                </fieldset>

                                <fieldset className={getClassNames([style['df-form-field'], !description.trim() && style.error])}>
                                    <Textarea
                                        label={l.descriptionPlaceholder}
                                        rows={2}
                                        resize='none'
                                        placeholder={l.descriptionPlaceholder}
                                        value={description}
                                        onChange={setDescription}
                                        className={style.input}

                                    />
                                </fieldset>

                                <fieldset className={getClassNames([style['df-form-field'], !assistantType && style.error])}>
                                    <SelectStatic
                                        label={l.assistantType}
                                        value={assistantType}
                                        options={allowedPersonaOptions.map(({ title: label, key: value }) => ({ label, value }))}
                                        onChange={setAssistantType}
                                        isDisabled={mode !== 'add'}
                                    />
                                </fieldset>

                                <fieldset className={getClassNames([style['df-form-field'], !category && style.error])}>
                                    <SelectStatic
                                        label={l.category}
                                        value={category}
                                        options={categoryOptions}
                                        onChange={setCategory}
                                        addEmptyPlaceholder={''}
                                    />
                                </fieldset>

                                <fieldset className={getClassNames([style['df-form-field'], !tags && style.error])}>
                                    <MultiSelect
                                        placeholder={''}
                                        label={labels.filterTags}
                                        value={tags}
                                        isSearchable={true}
                                        options={tagOptions}
                                        onChange={handleTagsChange}
                                    />
                                    <div className={getClassNames([style['chip-set']])}>
                                        <FilterChipSet
                                            chips={tags.map(tag => ({ id: tag, label: tag }))}
                                            onRemoveItem={handleRemoveTag}
                                        />
                                    </div>
                                </fieldset>

                                <fieldset className={style['df-form-field']}>
                                    <Checkbox
                                        label={l.isPublic}
                                        isChecked={isPublic}
                                        onChange={handleIsPublicChange}
                                    />
                                    {showPublicWarning && (
                                        <div className={getClassNames([style['warning-spacing']])}>
                                            <Alert type='warning'>
                                                <div>
                                                    {l.isPublicWarning}
                                                </div>
                                            </Alert>
                                        </div>
                                    )}
                                </fieldset>
                            </div>
                        } />}

                    right={<Column
                        headline='' content={
                            <div className={style['input-container']}>
                                <fieldset className={getClassNames([style['df-form-field'], !content.trim() && style.error])}>
                                    <MarkdownEditor
                                        label={l.contentPlaceholder}
                                        maxHeight={500}
                                        maxLength={3000}
                                        placeholder={l.contentPlaceholder}
                                        value={content}
                                        onChange={setContent}
                                        allowImageUpload={false}
                                        hideStatusBar={true}
                                    />
                                </fieldset>
                            </div>
                        }
                    />}
                />
            }
            footer={
                <>
                    <Button label={l.cancel} onClick={onClose} theme={ButtonThemes.textPrimary} />
                    {allowTryPrompt && (
                        <ButtonAsync
                            label={l.saveAndTryPromptButton}
                            onClick={handleSaveAndTry}
                            isDisabled={!isFormValid}
                        />
                    )}
                    <ButtonAsync
                        label={l.submit}
                        onClick={handleSubmit}
                        isDisabled={!isFormValid}
                    />
                </>
            }
        />
    );
};

export default PromptModal;

